import React, { useCallback, useEffect, useRef, useState } from "react";

import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import {
    Box,
    IconButton,
    Popover,
    PopoverOrigin,
    Typography,
} from "@mui/material";

const COPY_FEEDBACK_MS = 1400;
/** Time the card stays alive after the trigger is left, so the pointer can reach it. */
const HOVER_GRACE_MS = 180;
/** Dwell time on the trigger before the card opens, so passing over stays quiet. */
const HOVER_OPEN_DELAY_MS = 300;
/** Cap before the card starts scrolling instead of growing past the viewport. */
const DEFAULT_MAX_HEIGHT = 320;
/** Transparent strip above the card that bridges the visual gap to the trigger. */
const BRIDGE_HEIGHT = 8;

export const copyTextToClipboard = async (text: string): Promise<boolean> => {
    if (!text) return false;

    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        // Clipboard API can reject on insecure origins; fall back below.
    }

    try {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.top = "0";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand("copy");
        document.body.removeChild(textarea);
        return copied;
    } catch {
        return false;
    }
};

interface SummaryPopoverSectionProps {
    label: React.ReactNode;
    children: React.ReactNode;
    isLast?: boolean;
}

export const SummaryPopoverSection: React.FC<SummaryPopoverSectionProps> = ({
    label,
    children,
    isLast = false,
}) => (
    <Box
        sx={{
            padding: "10px 14px",
            borderBottom: isLast ? "none" : "1px solid #f2f2f4",
        }}
    >
        <Typography
            sx={{
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#86868b",
                fontFamily: '"Nunito", sans-serif',
                mb: "2px",
            }}
        >
            {label}
        </Typography>
        {children}
    </Box>
);

interface SummaryPopoverValueProps {
    children: React.ReactNode;
    /** Enables the copy affordance. Needs either a string child or `copyText`. */
    copyable?: boolean;
    /** Text placed on the clipboard when the child is not a plain string. */
    copyText?: string;
}

export const SummaryPopoverValue: React.FC<SummaryPopoverValueProps> = ({
    children,
    copyable = false,
    copyText,
}) => {
    const [copied, setCopied] = useState(false);
    const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (resetTimer.current) clearTimeout(resetTimer.current);
        },
        [],
    );

    const text = copyText ?? (typeof children === "string" ? children : "");
    const showCopy = copyable && Boolean(text);

    const handleCopy = useCallback(async () => {
        if (!(await copyTextToClipboard(text))) return;
        setCopied(true);
        if (resetTimer.current) clearTimeout(resetTimer.current);
        resetTimer.current = setTimeout(
            () => setCopied(false),
            COPY_FEEDBACK_MS,
        );
    }, [text]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "6px",
                "&:hover .summary-popover-copy": { opacity: 1 },
            }}
        >
            <Typography
                sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#1c1c1e",
                    fontFamily: '"Nunito", sans-serif',
                    lineHeight: 1.4,
                    overflowWrap: "anywhere",
                }}
            >
                {children}
            </Typography>
            {showCopy ? (
                <IconButton
                    className="summary-popover-copy"
                    size="small"
                    onClick={handleCopy}
                    aria-label={copied ? "Copied" : "Copy value"}
                    sx={{
                        p: "2px",
                        mt: "-1px",
                        flexShrink: 0,
                        color: copied ? "#2e7d32" : "#86868b",
                        opacity: copied ? 1 : 0,
                        transition: "opacity 0.15s ease, color 0.15s ease",
                        "&:hover": {
                            backgroundColor: "#f2f2f4",
                            color: copied ? "#2e7d32" : "#1c1c1e",
                        },
                        "&:focus-visible": { opacity: 1 },
                    }}
                >
                    {copied ? (
                        <CheckRoundedIcon sx={{ fontSize: 14 }} />
                    ) : (
                        <ContentCopyRoundedIcon sx={{ fontSize: 14 }} />
                    )}
                </IconButton>
            ) : null}
        </Box>
    );
};

interface SummaryPopoverProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    children: React.ReactNode;
    /** Lets the pointer enter the card so its contents stay selectable/copyable. */
    interactive?: boolean;
    onClose?: () => void;
    minWidth?: number;
    maxWidth?: number;
    /** Height at which the card body starts scrolling. */
    maxHeight?: number;
    /** Dwell time on the trigger before opening. */
    openDelay?: number;
    anchorOrigin?: PopoverOrigin;
    transformOrigin?: PopoverOrigin;
}

const defaultAnchorOrigin: PopoverOrigin = {
    vertical: "bottom",
    horizontal: "center",
};
const defaultTransformOrigin: PopoverOrigin = {
    vertical: "top",
    horizontal: "center",
};

/**
 * Hover card used for read-only summaries (mapping options, source fields).
 * In interactive mode it survives the trip from the trigger to the card, so
 * values can be copied instead of only being read.
 */
const SummaryPopover: React.FC<SummaryPopoverProps> = ({
    open,
    anchorEl,
    children,
    interactive = true,
    onClose,
    minWidth = 240,
    maxWidth = 320,
    maxHeight = DEFAULT_MAX_HEIGHT,
    openDelay = HOVER_OPEN_DELAY_MS,
    anchorOrigin = defaultAnchorOrigin,
    transformOrigin = defaultTransformOrigin,
}) => {
    const [visible, setVisible] = useState(open);
    const [pinnedAnchor, setPinnedAnchor] = useState<HTMLElement | null>(
        anchorEl,
    );
    const isPointerInside = useRef(false);

    useEffect(() => {
        // Every row receives the currently hovered anchor. A popover that is
        // still visible during the close grace period must keep its own anchor,
        // otherwise its stale content briefly jumps to the next hovered row.
        if (open && anchorEl) setPinnedAnchor(anchorEl);
    }, [open, anchorEl]);

    useEffect(() => {
        if (open) {
            // Already shown: re-opening (e.g. anchor swap) must not re-arm the delay.
            if (visible) return undefined;
            const timer = setTimeout(() => setVisible(true), openDelay);
            return () => clearTimeout(timer);
        }
        if (!interactive) {
            setVisible(false);
            return undefined;
        }
        const timer = setTimeout(() => {
            if (!isPointerInside.current) setVisible(false);
        }, HOVER_GRACE_MS);
        return () => clearTimeout(timer);
    }, [open, interactive, visible, openDelay]);

    const handlePointerLeave = useCallback(() => {
        isPointerInside.current = false;
        setVisible(false);
        onClose?.();
    }, [onClose]);

    const handlePointerEnter = useCallback(() => {
        isPointerInside.current = true;
    }, []);

    return (
        <Popover
            open={visible && Boolean(pinnedAnchor)}
            anchorEl={pinnedAnchor}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            onClose={onClose}
            sx={{
                pointerEvents: "none",
                zIndex: (theme) => theme.zIndex.modal - 1,
            }}
            slotProps={{
                paper: {
                    onMouseEnter: interactive ? handlePointerEnter : undefined,
                    onMouseLeave: interactive ? handlePointerLeave : undefined,
                    sx: {
                        pointerEvents: interactive ? "auto" : "none",
                        backgroundColor: "transparent",
                        backgroundImage: "none",
                        boxShadow: "none",
                        overflow: "visible",
                        paddingTop: `${BRIDGE_HEIGHT}px`,
                    },
                },
            }}
            hideBackdrop
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            disableScrollLock
        >
            <Box
                sx={{
                    background: "#ffffff",
                    border: "1px solid #e5e5ea",
                    borderRadius: "10px",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                    minWidth,
                    maxWidth,
                    maxHeight,
                    overflowY: "auto",
                    overflowX: "hidden",
                    overscrollBehavior: "contain",
                    "&::-webkit-scrollbar": { width: "8px" },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#d1d1d6",
                        borderRadius: "4px",
                    },
                }}
            >
                {children}
            </Box>
        </Popover>
    );
};

export default SummaryPopover;
